import { useState } from 'react';
import { useRatings } from '@/hooks/useRatings';
import { toast } from '@/store/toasts';
import styles from './StarRating.module.css';

interface StarRatingProps {
  slug: string;
  actId: string;
}

/**
 * 5-Sterne-Picker für einen Act. Zeigt das Crew-Aggregat (`avg ⭐ (count)`)
 * plus die eigene Wertung. Klick auf einen Stern feuert sofort POST
 * /api/ratings — optimistic Update über den Bulk-Cache lässt die
 * Anzeige nicht stottern.
 */
export function StarRating({ slug, actId }: StarRatingProps) {
  const { query, mutation } = useRatings(slug);
  const [hover, setHover] = useState<number>(0);

  const data = query.data;
  const myStars = data?.mine[actId]?.stars ?? 0;
  const agg = data?.byAct[actId] ?? { avg: 0, count: 0 };

  const display = hover || myStars;

  function onPick(stars: number) {
    if (mutation.isPending) return;
    mutation.mutate(
      { actId, stars },
      {
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Bewertung fehlgeschlagen');
        },
      },
    );
  }

  return (
    <div className={styles.row} onMouseLeave={() => setHover(0)}>
      <div className={styles.stars} role="radiogroup" aria-label="Bewertung in Sternen">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={myStars === n}
            aria-label={`${n} Stern${n === 1 ? '' : 'e'}`}
            className={`${styles.star} ${n <= display ? styles.starFilled : ''}`}
            onClick={() => onPick(n)}
            onMouseEnter={() => setHover(n)}
            disabled={mutation.isPending}
          >
            ★
          </button>
        ))}
      </div>
      {agg.count > 0 ? (
        <span className={styles.aggregate}>
          ⭐ {agg.avg.toFixed(1)} <small>({agg.count})</small>
        </span>
      ) : (
        <span className={styles.aggregateMuted}>noch keine Bewertung</span>
      )}
    </div>
  );
}
